import {
  buildCrmContactChannelActions,
  type CrmContactAction,
  type CrmContactActionGroup,
} from "@ease/crm/contact-action-groups";
import { getCrmActionHrefKind, normalizeCrmActionHref } from "@ease/crm/safe-action-hrefs";

export interface CrmContactChannelActionSource {
  email?: string | null;
  phone?: string | null;
}

export function buildCrmContactChannelActionGroup(
  contact: CrmContactChannelActionSource | null,
  customFields: unknown,
): CrmContactActionGroup | null {
  const actions: CrmContactAction[] = [];
  const emailHref = contact?.email ? normalizeCrmActionHref(`mailto:${contact.email}`) : null;
  const phoneHref = contact?.phone ? normalizeCrmActionHref(`tel:${contact.phone}`) : null;

  if (emailHref) {
    actions.push({
      key: "emailContact",
      label: "Email contact",
      description: "Open the contact's email address in the default mail client.",
      href: emailHref,
      tone: "primary",
      disabled: false,
      disabledReason: null,
    });
  }

  if (phoneHref) {
    actions.push({
      key: "callContact",
      label: "Call contact",
      description: "Call the contact's phone number from this device.",
      href: phoneHref,
      tone: "success",
      disabled: false,
      disabledReason: null,
    });
  }

  actions.push(...buildCrmContactChannelActions(customFields));

  const dedupedActions = dedupeCrmContactActions(actions);
  if (dedupedActions.length === 0) return null;

  return {
    key: "coreContactChannels",
    label: "Contact channels",
    description: "Safe actions from mounted contact fields and tenant-configured channel metadata.",
    source: "contact",
    actions: dedupedActions,
  };
}

export function dedupeCrmContactActions(actions: CrmContactAction[]): CrmContactAction[] {
  const seen = new Set<string>();
  return actions.filter((action) => {
    if (seen.has(action.href)) return false;
    seen.add(action.href);
    return true;
  });
}

export function buildCrmContactHeaderActions(groups: CrmContactActionGroup[]): CrmContactAction[] {
  return groups
    .flatMap((group) => group.actions)
    .filter((action) => !action.disabled && Boolean(getCrmActionHrefKind(action.href)))
    .slice(0, 4);
}
